/**
 * @module VerifyVoIPConstants
 * @description Phase 10 extraction of VoIP-specific verify status constants, polling limits, and translations.
 * Keeps VoIP strategy configuration isolated from shared verify constants.
 */

import { t } from '@lingui/macro';

// Re-export from shared constants (single source of truth)
export { QUICK_TEST_STATUS } from '../../../verify/VerifySharedConstants';

/**
 * VoIP party status single-letter codes (from quickTests.constants.js VOIP_QUICK_TEST_PARTY_STATUSES).
 */
export const VOIP_PARTY_STATUS = {
    INITIALIZING: 'I',
    CONFIGURED: 'O',
    QUEUED: 'Q',
    WAITING: 'W',
    PLACING_CALL: 'P',
    CALL_ACCEPTED: 'A',
    RUNNING: 'R',
    TEARDOWN: 'T',
    HANGUP: 'H',
    ENDED: 'F',
    COMPLETED: 'C',
    ERROR: 'E',
    UNANSWERED: 'U',
    UNREGISTERED: 'D',
    TERMINATED: 'M',
    UNEXPECTED_HANGUP: 'G',
    UNABLE_TO_PLACE_CALL: 'L',
    UNABLE_TO_PLACE_CALL_DNS: 'J',
    UNABLE_TO_PLACE_CALL_URI: 'K',
    UNABLE_TO_PLACE_CALL_PHONE: 'Y',
    UNABLE_TO_RECEIVE_CALL: 'V',
    NO_MEDIA: 'AC',
    NPOINT_DTMF_FAILED: 'AA',
    WRONG_NPOINT_DTMF_FAILED: 'AB',
    EXPIRED: 'B',
    CANCELED: 'X',
    PHONE: 'N',
    REGISTER_WAV_FAILURE: 'Z',
};

/** VoIP registration statuses that allow verify */
export const VOIP_REGISTERED_STATUSES = new Set(['Registered', 'Direct Calling']);

/** Verify test default duration and jitter (from verify.service.js) */
export const VERIFY_DURATION = 10;
export const VERIFY_JITTER = 50;

/** Polling interval for VoIP verify (ms) */
export const POLL_INTERVAL_MS = 2000;

/** Maximum polling duration (seconds) */
export const POLL_COUNT_MAX = 300;

/**
 * Translate a VoIP party status code + role to a human-readable message.
 * Must be called at runtime (inside a function/component), not at module level.
 * portal/ui source: i18n key config.tests.voipPartyStatuses.<CODE><Role>
 *
 * @param {string} statusCode - Single-letter status code (e.g. 'U', 'D')
 * @param {string} role - 'Caller' or 'Callee'
 * @returns {string} Translated status message
 */
export const translateVoipPartyStatus = (statusCode, role = '') => {
    const code = (statusCode || '').toUpperCase();
    const STATUSES = {
        ICaller: t`Caller is initializing`,
        ICallee: t`Callee is initializing`,
        OCaller: t`Caller is configured`,
        OCallee: t`Callee is configured`,
        QCaller: t`Caller is queued`,
        QCallee: t`Callee is queued`,
        WCaller: t`Caller is waiting for callee`,
        WCallee: t`Callee is waiting for caller`,
        PCaller: t`Caller is placing call`,
        PCallee: t`Callee is receiving call`,
        ACaller: t`Caller accepted call`,
        ACallee: t`Callee accepted call`,
        RCaller: t`Caller is running`,
        RCallee: t`Callee is running`,
        TCaller: t`Caller is tearing down`,
        TCallee: t`Callee is tearing down`,
        HCaller: t`Caller hung up`,
        HCallee: t`Callee hung up`,
        FCaller: t`Caller ended`,
        FCallee: t`Callee ended`,
        CCaller: t`Caller completed successfully`,
        CCallee: t`Callee completed successfully`,
        ECaller: t`Caller encountered an error`,
        ECallee: t`Callee encountered an error`,
        UCaller: t`The caller placed a call, but was unanswered before the ring duration timeout`,
        UCallee: t`The caller placed a call, but was unanswered before the ring duration timeout`,
        DCaller: t`The Callee is not currently, or not able to register with the call manager`,
        DCallee: t`The Caller is not currently, or not able to register with the call manager`,
        MCaller: t`The call was terminated`,
        MCallee: t`The call was terminated`,
        GCaller: t`The Callee hung up`,
        GCallee: t`The Caller hung up`,
        LCaller: t`The caller was unable to place the call`,
        LCallee: t`The caller was unable to place the call`,
        JCaller: t`The caller was unable to place the call, failed to resolve callee host name`,
        JCallee: t`The caller was unable to place the call, failed to resolve callee host name`,
        KCaller: t`The caller was unable to place the call, invalid callee URI`,
        KCallee: t`The caller was unable to place the call, invalid callee URI`,
        YCaller: t`The caller was unable to place the call, invalid callee phone number`,
        YCallee: t`The caller was unable to place the call, invalid callee phone number`,
        VCaller: t`The callee never received the call`,
        VCallee: t`The callee never received the call`,
        BCaller: t`Expired, timeout reached`,
        BCallee: t`Expired, timeout reached`,
        XCaller: t`Canceled - The call was canceled`,
        XCallee: t`Canceled - The call was canceled`,
        ACCaller: t`Call failed, no media present`,
        ACCallee: t`Call failed, no media present`,
        AACaller: t`Call failed, not an nPoint or DTMF failed`,
        AACallee: t`Call failed, not an nPoint or DTMF failed`,
        ABCaller: t`Call failed, wrong nPoint or DTMF failed`,
        ABCallee: t`Call failed, wrong nPoint or DTMF failed`,
        NCaller: t`Phone`,
        NCallee: t`Phone`,
        ZCaller: t`The application had an issue finding the Wav file to play for the phone call`,
        ZCallee: t`The application had an issue finding the Wav file to play for the phone call`,
    };

    return STATUSES[`${code}${role}`] || STATUSES[code] || statusCode;
};
